import React from 'react';
import { Box, Container, Typography, Alert } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useAuth, vortxTypography } from '@vortx/shared-ui';
import { useThemeMode } from '@vortx/shared-ui';
import AdminNav from './AdminNav';

// Retro mode cascading styles for admin pages
const retroAdminStyles = {
  // Cards and Paper
  '& .MuiCard-root, & .MuiPaper-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    borderRadius: 0,
    border: '1px solid rgba(63, 106, 100, 0.4)',
    boxShadow: 'none',
  },
  // TextFields and FormControls
  '& .MuiTextField-root, & .MuiFormControl-root': {
    '& .MuiOutlinedInput-root': {
      fontFamily: vortxTypography.mono,
      fontSize: '0.85rem',
      color: '#c8c8c8',
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      borderRadius: 0,
      '& fieldset': {
        borderColor: 'rgba(63, 106, 100, 0.4)',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(95, 212, 196, 0.6)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#5fd4c4',
        borderWidth: '1px',
        boxShadow: '0 0 8px rgba(95, 212, 196, 0.25)',
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: vortxTypography.mono,
      fontSize: '0.75rem',
      color: '#888',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      '&.Mui-focused': {
        color: '#5fd4c4',
      },
    },
    '& .MuiInputBase-input': {
      color: '#c8c8c8',
    },
    '& .MuiFormHelperText-root': {
      fontFamily: vortxTypography.mono,
      fontSize: '0.65rem',
      color: '#888',
    },
  },
  '& .MuiSelect-icon': {
    color: '#888',
  },
  // Checkboxes
  '& .MuiCheckbox-root': {
    color: 'rgba(63, 106, 100, 0.6)',
    '&.Mui-checked': {
      color: '#5fd4c4',
    },
  },
  '& .MuiFormControlLabel-label': {
    fontFamily: vortxTypography.mono,
    fontSize: '0.8rem',
    color: '#c8c8c8',
  },
  // Buttons
  '& .MuiButton-root': {
    fontFamily: vortxTypography.mono,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    borderRadius: 0,
  },
  '& .MuiButton-outlined': {
    borderWidth: '1px',
    '&:hover': {
      borderWidth: '1px',
    },
  },
  '& .MuiButton-contained': {
    boxShadow: 'none',
    '&:hover': {
      boxShadow: '0 0 15px rgba(178, 140, 80, 0.4)',
    },
  },
  '& .MuiIconButton-root': {
    borderRadius: 0,
  },
  // Tables
  '& .MuiTable-root': {
    '& .MuiTableCell-root': {
      fontFamily: vortxTypography.mono,
      borderBottom: '1px solid rgba(63, 106, 100, 0.3)',
      color: '#c8c8c8',
      fontSize: '0.8rem',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      color: '#b28c50',
      fontSize: '0.7rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
    '& .MuiTableRow-root:hover': {
      backgroundColor: 'rgba(95, 212, 196, 0.08)',
    },
  },
  '& .MuiTableContainer-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    border: '1px solid rgba(63, 106, 100, 0.4)',
    borderRadius: 0,
  },
  '& .MuiTablePagination-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderTop: '1px solid rgba(63, 106, 100, 0.3)',
    color: '#888',
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      fontFamily: vortxTypography.mono,
      fontSize: '0.75rem',
    },
    '& .MuiIconButton-root': {
      color: '#888',
    },
  },
  // Chips
  '& .MuiChip-root': {
    fontFamily: vortxTypography.mono,
    fontSize: '0.7rem',
    borderRadius: 0,
    height: 'auto',
    padding: '4px 8px',
  },
  // Alerts
  '& .MuiAlert-root': {
    fontFamily: vortxTypography.mono,
    borderRadius: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    border: '1px solid rgba(63, 106, 100, 0.4)',
    '& .MuiAlert-message': {
      color: '#c8c8c8',
    },
  },
  '& .MuiAlert-standardWarning': {
    borderColor: '#f5a623',
    backgroundColor: 'rgba(245, 166, 35, 0.1)',
  },
  '& .MuiAlert-standardError': {
    borderColor: '#e74c3c',
    backgroundColor: 'rgba(231, 76, 60, 0.1)',
  },
  '& .MuiAlert-standardSuccess': {
    borderColor: '#00ff41',
    backgroundColor: 'rgba(0, 255, 65, 0.08)',
  },
  '& .MuiAlert-standardInfo': {
    borderColor: '#5fd4c4',
    backgroundColor: 'rgba(95, 212, 196, 0.1)',
  },
  // Tabs
  '& .MuiTabs-root': {
    '& .MuiTab-root': {
      fontFamily: vortxTypography.mono,
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      color: '#888',
      '&.Mui-selected': {
        color: '#b28c50',
      },
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#b28c50',
    },
  },
  // Dialogs
  '& .MuiDialog-paper': {
    backgroundColor: '#1a1a1a',
    border: '1px solid rgba(63, 106, 100, 0.4)',
    borderRadius: 0,
  },
  '& .MuiDialogTitle-root': {
    fontFamily: vortxTypography.heading,
    color: '#b28c50',
    borderBottom: '1px solid rgba(63, 106, 100, 0.3)',
  },
  '& .MuiDialogContent-root': {
    backgroundColor: 'transparent',
  },
  '& .MuiDialogActions-root': {
    borderTop: '1px solid rgba(63, 106, 100, 0.3)',
  },
  // Dividers
  '& .MuiDivider-root': {
    borderColor: 'rgba(63, 106, 100, 0.3)',
  },
  // Typography
  '& .MuiTypography-h4, & .MuiTypography-h5, & .MuiTypography-h6': {
    fontFamily: vortxTypography.heading,
    color: '#b28c50',
  },
  '& .MuiTypography-body1, & .MuiTypography-body2': {
    fontFamily: vortxTypography.mono,
    color: '#c8c8c8',
  },
  // Progress indicators
  '& .MuiLinearProgress-root': {
    backgroundColor: 'rgba(63, 106, 100, 0.3)',
    borderRadius: 0,
    '& .MuiLinearProgress-bar': {
      backgroundColor: '#5fd4c4',
    },
  },
  '& .MuiCircularProgress-root': {
    color: '#5fd4c4',
  },
};

const AdminLayout = ({ children, title, requireSuperAdmin = false, maxWidth = "lg" }) => {
  const { userProfile } = useAuth();
  const { isRetro } = useThemeMode();

  const isAdmin = userProfile?.is_super_admin || userProfile?.is_company_admin;
  const isSuperAdmin = userProfile?.is_super_admin;

  if (!isAdmin) {
    return <Navigate to="/no-access" replace />;
  }

  if (requireSuperAdmin && !isSuperAdmin) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          This section requires super admin access.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: isRetro ? 'auto' : '100vh', ...(isRetro && retroAdminStyles) }}>
      {!isRetro && <AdminNav isSuperAdmin={isSuperAdmin} />}
      <Box component="main" sx={{ flexGrow: 1, p: isRetro ? 0 : 3 }}>
        <Container maxWidth={maxWidth}>
          {title && (
            <Typography
              variant="h4"
              gutterBottom
              sx={isRetro ? {
                fontFamily: vortxTypography.heading,
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#b28c50',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                textShadow: '0 0 15px rgba(178, 140, 80, 0.3)',
                mb: 3,
              } : { mb: 3 }}
            >
              {title}
            </Typography>
          )}
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default AdminLayout;
